import {ReactNode} from "react";
import {Box, Typography} from "@mui/material";
import AnchorIcon from "@mui/icons-material/Anchor";
import {ElevationApp} from "../../ElevationApp";
import {sticksById} from "../../model/frame/MdFrameBlock";
import {useEventEmitter2} from "../../util/useEventEmitter2";
import {RibbonButton, RibbonGroup, RibbonIconButton, RibbonStack, RibbonStepper} from "../Ribbon";
import {anchorSetupAllowed, startAnchorSetup} from "../AnchorSetupTab";
import {MIN_SECTION_LENGTH, setAnchorQty, setAnchorSectionCount} from "../../scenes/frameGrid/Anchors";
import {emitChanged} from "./helpers";

// the anchors of the selected stick piece, when it can take anchors. With no anchors it
// offers only Setup Anchors; with anchors it edits the qty and the section count.
export function StickAnchorsGroup(props: {app: ElevationApp; pieceId: string}) {
    const app = props.app;
    useEventEmitter2(app.frameSpec.frameBlocksChanged);
    if(!anchorSetupAllowed(app)) return null;

    const stickId = app.frameGrid?.anchorMarks.targetOf(props.pieceId) ?? null;
    if(!stickId) return null;

    const anchors = sticksById(app.frameSpec.frameBlocks).get(stickId)?.anchors;
    const layout = app.frameGrid?.anchorMarks.layoutOf(stickId) ?? null;

    if(!anchors || !layout) {
        return (
            <RibbonGroup label="Anchors">
                <RibbonButton icon={<AnchorIcon />} label="Setup Anchors" onClick={() => startAnchorSetup(app)} />
            </RibbonGroup>
        );
    }

    const sections = anchors.sections;
    const maxSections = Math.max(1, Math.floor(layout.length / MIN_SECTION_LENGTH));

    return (
        <RibbonGroup label="Anchors">
            <RibbonStack>
                <Row label="Qty">
                    {sections.length === 1 ? (
                        <RibbonStepper
                            value={layout.sections[0].qty}
                            min={0}
                            onChange={qty => setAnchorQty(app.frameSpec, stickId, 0, qty) && emitChanged(app)}
                        />
                    ) : (
                        <Typography sx={{fontSize: 13, px: 0.5}}>
                            {layout.sections.reduce((sum, s) => sum + s.qty, 0)}
                        </Typography>
                    )}
                </Row>
                <Row label="Sections">
                    <RibbonStepper
                        value={sections.length}
                        min={1}
                        max={maxSections}
                        onChange={count => setAnchorSectionCount(app.frameSpec, stickId, count, layout.length) && emitChanged(app)}
                    />
                </Row>
            </RibbonStack>
            <RibbonIconButton icon={<AnchorIcon />} label="Setup Anchors" onClick={() => startAnchorSetup(app)} />
        </RibbonGroup>
    );
}

// one labelled line of the stacked anchor controls.
function Row(props: {label: string; children: ReactNode}) {
    return (
        <Box sx={{display: "flex", alignItems: "center", height: 28}}>
            <Typography sx={{fontSize: 12, width: 56}}>{props.label}</Typography>
            {props.children}
        </Box>
    );
}
